import Konva from "konva";
import { useEffect, useRef } from "react";

// Animates Konva nodes named "running" on the given layer.
// Two styles are supported (set via node.attrs.runStyle):
//   - "dash" (default): marching-ants — animates dashOffset on the node itself.
//   - "pulse": pulses opacity on each direct child (e.g. dots of a chip),
//     with a per-child phase offset so children appear to chase.
export const useRunningAnimation = (layerRef, offsetRef) => {
  const animRef = useRef(null);
  const isRunningRef = useRef(false);
  const targetsRef = useRef([]); // cache of shapes to animate

  useEffect(() => {
    const start = () => {
      if (animRef.current) return;
      animRef.current = new Konva.Animation((frame) => {
        const timeDiff = frame?.timeDiff ?? 16.7;
        const dashStep = (1.2 * timeDiff) / 16.7;
        offsetRef.current -= dashStep;
        const t = (frame?.time ?? 0) / 1000;
        const targets = targetsRef.current;
        for (let i = 0; i < targets.length; i++) {
          const target = targets[i];
          const style = target.attrs?.runStyle;
          if (style === "pulse") {
            const dots = target.getChildren ? target.getChildren() : [];
            const n = Math.max(1, dots.length);
            for (let j = 0; j < dots.length; j++) {
              // 1.4s full cycle; phase-offset per dot
              const phase = (t * (Math.PI * 2) / 1.4) - (j * (Math.PI * 2) / n);
              const o = 0.25 + 0.75 * ((Math.sin(phase) + 1) / 2);
              dots[j].opacity(o);
            }
          } else {
            target.dashOffset(offsetRef.current);
          }
        }
      }, layerRef.current);
      animRef.current.start();
      isRunningRef.current = true;
    };

    const stop = () => {
      animRef.current?.stop();
      animRef.current = null;
      isRunningRef.current = false;
    };

    const checkAndUpdateAnimation = () => {
      const layer = layerRef.current;
      if (!layer) return;

      const running = layer.find('.running'); // every 300ms, not every frame
      const hasRunning = running.length > 0;

      // cache the collection as a plain array for fast iteration
      targetsRef.current = running.toArray ? running.toArray() : running;

      if (hasRunning && !isRunningRef.current) start();
      else if (!hasRunning && isRunningRef.current) stop();
    };

    const interval = setInterval(checkAndUpdateAnimation, 300);
    return () => {
      clearInterval(interval);
      animRef.current?.stop();
      animRef.current = null;
      isRunningRef.current = false;
      targetsRef.current = [];
    };
  }, [layerRef, offsetRef]);
};
